import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { setAppLanguage, SUPPORTED_LANGUAGES } from "@/i18n";

export function LanguageSwitcher({ variant = "full" }) {
  const { t, i18n } = useTranslation();
  const { user, refresh } = useAuth();
  const current = (i18n.language || "en").split("-")[0];

  const choose = async (code) => {
    if (code === current) return;
    setAppLanguage(code);
    if (user) {
      try {
        await api.put("/account/language", { language: code });
        await refresh();
        toast.success(t("language.updated"));
      } catch {
        toast.error(t("common.somethingWrong"));
      }
    }
  };

  if (variant === "compact") {
    return (
      <div className="inline-flex rounded-full bg-[#ECECEC] p-0.5" data-testid="language-switcher-compact">
        {SUPPORTED_LANGUAGES.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => choose(l.code)}
            data-testid={`lang-option-${l.code}`}
            className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase transition-colors ${
              current === l.code ? "bg-white text-[#202123] border border-[#C5C5D0]" : "text-[#6E6E80] hover:text-[#202123]"
            }`}
            title={l.native}
          >
            {l.code}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div data-testid="language-switcher">
      <div className="flex items-center gap-2 mb-1.5">
        <Globe className="h-4 w-4 text-[#202123]" />
        <span className="font-medium text-navy">{t("language.label")}</span>
      </div>
      <p className="text-sm text-slatebody mb-3">{t("language.description")}</p>
      <div className="flex gap-2">
        {SUPPORTED_LANGUAGES.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => choose(l.code)}
            data-testid={`lang-choice-${l.code}`}
            className={`flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${
              current === l.code
                ? "border-[#C5C5D0] bg-[#ECECEC]/10 text-navy"
                : "border-slate-200 text-slatebody hover:border-[#C5C5D0]/50"
            }`}
          >
            {l.native}
          </button>
        ))}
      </div>
    </div>
  );
}
